import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, LayoutGrid, Map as MapIcon, X, Check, Heart } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PropertyCard } from "@/components/site/PropertyCard";

const FILTERS = [
  { key: "events", label: "Weddings & Events" },
  { key: "pool", label: "Pool" }, { key: "hot_tub", label: "Hot Tub" },
  { key: "lakefront", label: "Waterfront" }, { key: "pet", label: "Pet Friendly" },
  { key: "luxury", label: "Luxury" }, { key: "fire_pit", label: "Fire Pit" },
  { key: "game_room", label: "Game Room" }, { key: "family", label: "Family Friendly" },
];

export default function Rentals() {
  const [sp] = useSearchParams();
  const [props, setProps] = useState([]);
  const [view, setView] = useState("grid");
  const [showFilters, setShowFilters] = useState(false);
  const [q, setQ] = useState(sp.get("q") || "");
  const [beds, setBeds] = useState("any");
  const [baths, setBaths] = useState("any");
  const [guests, setGuests] = useState(sp.get("guests") || "any");
  const [price, setPrice] = useState([100, 3000]);
  const [amenities, setAmenities] = useState(sp.get("amenity") ? [sp.get("amenity")] : []);
  const [sort, setSort] = useState("rating");

  const fetchProps = useCallback(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (beds !== "any") p.set("bedrooms", beds);
    if (baths !== "any") p.set("bathrooms", baths);
    if (guests !== "any") p.set("guests", guests);
    p.set("min_price", price[0]); p.set("max_price", price[1]); p.set("sort", sort);
    amenities.forEach((a) => p.set("amenity", a));
    api.get(`/properties?${p.toString()}`).then((r) => setProps(r.data.properties));
  }, [q, beds, baths, guests, price, amenities, sort]);

  useEffect(() => { fetchProps(); }, [fetchProps]);

  const toggle = (k) => setAmenities((a) => a.includes(k) ? a.filter((x) => x !== k) : [...a, k]);

  return (
    <div className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
      <div className="mb-6">
        <span className="overline">Vacation Rentals</span>
        <h1 className="mt-2 text-4xl lg:text-5xl">Find your Texas escape</h1>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]">
          <Input data-testid="rentals-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by city or property…" className="h-11 rounded-full border-black/10 bg-white px-5" />
        </div>
        <Button data-testid="filters-toggle" variant="outline" onClick={() => setShowFilters((s) => !s)}
          className="h-11 rounded-full border-black/10">
          <SlidersHorizontal size={16} className="mr-2" /> Filters
        </Button>
        <Button data-testid="events-quick-filter" variant="outline" onClick={() => toggle("events")}
          className={`h-11 rounded-full ${amenities.includes("events") ? "btn-ink border-transparent text-white" : "border-camel text-camel hover:bg-camel hover:text-white"}`}>
          <Heart size={15} className="mr-2" /> Weddings &amp; Events
        </Button>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="rentals-sort" className="h-11 w-44 rounded-full border-black/10 bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="rating">Top rated</SelectItem>
            <SelectItem value="price_asc">Price: Low to High</SelectItem>
            <SelectItem value="price_desc">Price: High to Low</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex rounded-full border border-black/10 bg-white p-1">
          <button data-testid="view-grid" onClick={() => setView("grid")}
            className={`rounded-full p-2 ${view === "grid" ? "bg-ink text-white" : "text-ink"}`}><LayoutGrid size={18} /></button>
          <button data-testid="view-map" onClick={() => setView("map")}
            className={`rounded-full p-2 ${view === "map" ? "bg-ink text-white" : "text-ink"}`}><MapIcon size={18} /></button>
        </div>
      </div>

      {showFilters && (
        <div data-testid="filters-panel" className="mb-8 rounded-3xl border border-black/5 bg-white p-6">
          <div className="grid gap-6 md:grid-cols-4">
            <div>
              <div className="overline mb-2">Bedrooms</div>
              <Select value={beds} onValueChange={setBeds}>
                <SelectTrigger className="rounded-full"><SelectValue /></SelectTrigger>
                <SelectContent>{["any", "2", "3", "4", "5"].map((n) => <SelectItem key={n} value={n}>{n === "any" ? "Any" : `${n}+`}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <div className="overline mb-2">Bathrooms</div>
              <Select value={baths} onValueChange={setBaths}>
                <SelectTrigger className="rounded-full"><SelectValue /></SelectTrigger>
                <SelectContent>{["any", "2", "3", "4"].map((n) => <SelectItem key={n} value={n}>{n === "any" ? "Any" : `${n}+`}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <div className="overline mb-2">Guests</div>
              <Select value={guests} onValueChange={setGuests}>
                <SelectTrigger className="rounded-full"><SelectValue /></SelectTrigger>
                <SelectContent>{["any", "4", "8", "12", "16"].map((n) => <SelectItem key={n} value={n}>{n === "any" ? "Any" : `${n}+`}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <div className="overline mb-2">Price ${price[0]} – ${price[1]}</div>
              <Slider min={100} max={3000} step={50} value={price} onValueChange={setPrice} className="mt-4" />
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button key={f.key} type="button" data-testid={`filter-${f.key}`} onClick={() => toggle(f.key)}
                className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors ${
                  amenities.includes(f.key) ? "border-ink bg-ink text-white" : "border-black/10 text-ink hover:border-camel"}`}>
                <span className={`flex h-3.5 w-3.5 items-center justify-center rounded-[4px] border ${amenities.includes(f.key) ? "border-white bg-white" : "border-current"}`}>
                  {amenities.includes(f.key) && <Check size={10} className="text-ink" />}
                </span> {f.label}
              </button>
            ))}
            {amenities.length > 0 && (
              <button data-testid="clear-filters" onClick={() => setAmenities([])}
                className="flex items-center gap-1 rounded-full px-3 py-2 text-sm text-taupe hover:text-ink">
                <X size={14} /> Clear
              </button>
            )}
          </div>
        </div>
      )}

      <div className="mb-4 text-sm text-taupe">{props.length} homes available</div>

      {view === "grid" ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {props.map((p, i) => <PropertyCard key={p.id} p={p} index={i} />)}
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="overflow-hidden rounded-3xl border border-black/5">
            <iframe title="map" className="h-full min-h-[500px] w-full"
              src="https://www.openstreetmap.org/export/embed.html?bbox=-98.75%2C29.30%2C-98.05%2C30.00&layer=mapnik" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {props.map((p, i) => <PropertyCard key={p.id} p={p} index={i} />)}
          </div>
        </div>
      )}

      {props.length === 0 && (
        <div className="py-20 text-center text-taupe">No homes match your filters. Try widening your search.</div>
      )}
    </div>
  );
}
